import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { usePortfolio } from "@/context/PortfolioContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { StatCard } from "@/components/StatCard";
import { fmtCurrency, fmtSigned, fmtPct, gainClass } from "@/lib/format";
import { Loader2, Calculator } from "lucide-react";
import { toast } from "sonner";

function ScenarioResult({ result }) {
    return (
        <>
            <div className="grid grid-cols-2 gap-4">
                <StatCard testId="whatif-position-change" label={`${result.target} Change`} value={fmtSigned(result.position_change)} delta={result.position_change} deltaPct={fmtPct(result.change_percent)} />
                <StatCard testId="whatif-new-value" label="New Portfolio Value" value={fmtCurrency(result.new_portfolio_value)} delta={result.position_change} deltaPct={fmtPct(result.portfolio_change_pct)} />
            </div>
            <Card className="p-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Current portfolio value</span><span className="num">{fmtCurrency(result.current_portfolio_value)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Affected value ({result.target})</span><span className="num">{fmtCurrency(result.affected_value)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Impact</span><span className={`num ${gainClass(result.position_change)}`}>{fmtSigned(result.position_change)}</span></div>
                <div className="flex justify-between font-semibold border-t border-border pt-3"><span>New portfolio value</span><span className="num">{fmtCurrency(result.new_portfolio_value)}</span></div>
                <p className="text-xs text-muted-foreground pt-2">{result.disclaimer}</p>
            </Card>
        </>
    );
}

export function WhatIfTab() {
    const { subs } = usePortfolio();
    const [target, setTarget] = useState("PORTFOLIO");
    const [pct, setPct] = useState(-20);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const holdings = useQuery({ queryKey: ["whatif-holdings"], queryFn: async () => (await api.get(`/portfolio/holdings?sub=ALL`)).data });
    const tickers = [...new Set((holdings.data?.holdings || []).map((h) => h.ticker))];

    const run = async () => {
        setLoading(true);
        try { const { data } = await api.post("/whatif", { target, change_percent: pct }); setResult(data); }
        catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-5 space-y-5">
                <div className="flex items-center gap-2"><Calculator className="w-5 h-5 text-primary" /><h2 className="font-heading text-lg font-semibold">Scenario Calculator</h2></div>
                <div className="space-y-1.5">
                    <label className="text-sm font-medium">What changes?</label>
                    <Select value={target} onValueChange={setTarget}>
                        <SelectTrigger data-testid="whatif-target-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="PORTFOLIO">Entire Portfolio</SelectItem>
                            {subs.map((s) => <SelectItem key={s.id} value={`sub:${s.name}`}>Sub: {s.name}</SelectItem>)}
                            {tickers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between text-sm"><label className="font-medium">Price change</label><span className={`num font-semibold ${gainClass(pct)}`}>{fmtPct(pct)}</span></div>
                    <Slider value={[pct]} min={-50} max={50} step={1} onValueChange={(v) => setPct(v[0])} data-testid="whatif-slider" />
                </div>
                <Button onClick={run} disabled={loading} className="w-full" data-testid="whatif-run-btn">
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Calculate scenario
                </Button>
                <p className="text-xs text-muted-foreground">Purely mathematical projection — not a prediction of future prices.</p>
            </Card>

            <div className="space-y-4">
                {result ? <ScenarioResult result={result} /> : (
                    <Card className="p-8 h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                        <Calculator className="w-8 h-8 mb-3 opacity-50" />
                        <p className="text-sm">Configure a scenario and click Calculate to see the projected impact.</p>
                    </Card>
                )}
            </div>
        </div>
    );
}
